/**
 * Geographic coordinates for federation client sites.
 *
 * The backend ClientSite contract does not (yet) carry lat/lng, so the
 * Federation Map resolves coordinates here, keyed by the opaque `site_id`.
 * A secondary lookup by `organization_id` and a final city-name heuristic
 * keep the map populated even if a new site appears before this table is
 * updated. Coordinates are approximate (campus centroids) and are used for
 * presentation only — never for ACL or routing.
 */

/** @type {Record<string, {city: string, province: string, lat: number, lng: number}>} */
export const SITE_GEO_BY_ID = {
  site_tgh_01:   { city: "Toronto",   province: "ON", lat: 43.6579, lng: -79.3873 }, // Toronto Grand Hospital
  site_smh_01:   { city: "Toronto",   province: "ON", lat: 43.6534, lng: -79.3784 }, // South Metro Hospital
  site_sun_01:   { city: "Toronto",   province: "ON", lat: 43.7227, lng: -79.3766 }, // Sunridge Health Sciences Centre
  site_mtl_01:   { city: "Montréal",  province: "QC", lat: 45.4969, lng: -73.5878 }, // Montréal Metropolitan Hospital
  site_oicr_01:  { city: "Toronto",   province: "ON", lat: 43.6596, lng: -79.3886 }, // Ontario Institute for Clinical Research
  site_bccrc_01: { city: "Vancouver", province: "BC", lat: 49.2627, lng: -123.1207 }, // Beacon Coast Cancer Research Centre
};

/** Fallback by organization_id when a site_id isn't in the table above. */
export const SITE_GEO_BY_ORG = {
  org_uhn:        { city: "Toronto",   province: "ON", lat: 43.6579, lng: -79.3873 },
  org_unity:      { city: "Toronto",   province: "ON", lat: 43.6534, lng: -79.3784 },
  org_sunnybrook: { city: "Toronto",   province: "ON", lat: 43.7227, lng: -79.3766 },
  org_mcgill:     { city: "Montréal",  province: "QC", lat: 45.4969, lng: -73.5878 },
  org_oicr:       { city: "Toronto",   province: "ON", lat: 43.6596, lng: -79.3886 },
  org_bccrc:      { city: "Vancouver", province: "BC", lat: 49.2627, lng: -123.1207 },
};

/** Last-resort heuristic by a city keyword found in the display name. */
const CITY_KEYWORDS = [
  { match: /toronto|sunnybrook|st\.?\s*michael|ontario institute/i, geo: { city: "Toronto",   province: "ON", lat: 43.6579, lng: -79.3873 } },
  { match: /montr[ée]al|mcgill/i,                                   geo: { city: "Montréal",  province: "QC", lat: 45.4969, lng: -73.5878 } },
  { match: /\bbc\b|british columbia|vancouver/i,                    geo: { city: "Vancouver", province: "BC", lat: 49.2627, lng: -123.1207 } },
  { match: /ottawa/i,                                               geo: { city: "Ottawa",    province: "ON", lat: 45.4215, lng: -75.6972 } },
  { match: /calgary/i,                                              geo: { city: "Calgary",   province: "AB", lat: 51.0447, lng: -114.0719 } },
  { match: /edmonton/i,                                             geo: { city: "Edmonton",  province: "AB", lat: 53.5461, lng: -113.4938 } },
  { match: /winnipeg/i,                                             geo: { city: "Winnipeg",  province: "MB", lat: 49.8951, lng: -97.1384 } },
  { match: /halifax/i,                                              geo: { city: "Halifax",   province: "NS", lat: 44.6488, lng: -63.5752 } },
  { match: /qu[ée]bec/i,                                            geo: { city: "Québec",    province: "QC", lat: 46.8139, lng: -71.2080 } },
];

/**
 * Resolve a site to a geo entry, or null if no coordinate can be inferred.
 * @param {{site_id?: string, organization_id?: string, display_name?: string, organization_display_name?: string}} site
 */
export const resolveSiteGeo = (site) => {
  if (!site) return null;
  if (site.site_id && SITE_GEO_BY_ID[site.site_id]) return SITE_GEO_BY_ID[site.site_id];
  if (site.organization_id && SITE_GEO_BY_ORG[site.organization_id]) return SITE_GEO_BY_ORG[site.organization_id];
  const haystack = `${site.display_name || ""} ${site.organization_display_name || ""}`;
  const hit = CITY_KEYWORDS.find((c) => c.match.test(haystack));
  return hit ? hit.geo : null;
};
